import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import DOMPurify from "dompurify";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/new.scss";
import "../../News/AddEdit/addnew.scss";

const statusOptions = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

const employmentTypeOptions = [
  { value: "Full Time", label: "Full Time" },
  { value: "Part Time", label: "Part Time" },
  { value: "Contract", label: "Contract" },
  { value: "Internship", label: "Internship" },
  { value: "Temporary", label: "Temporary" },
];

// Local YYYY-MM-DD for the <input type="date"> default.
const todayISO = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

const CareersAddEdit = () => {
  const { id } = useParams();
  const isAdd = !id;
  const navigate = useNavigate();
  const token = localStorage.getItem("accessToken");

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [employmentType, setEmploymentType] = useState(employmentTypeOptions[0]);
  const [experience, setExperience] = useState("");
  const [email, setEmail] = useState("");
  const [datePosted, setDatePosted] = useState(todayISO());
  const [validThrough, setValidThrough] = useState("");
  const [status, setStatus] = useState(statusOptions[0]);
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchById = async () => {
      try {
        setLoading(true);
        const response = await axios({
          method: "post",
          url: `${process.env.REACT_APP_API}admin/get-joblisting-byid`,
          headers: { "x-access-token": token },
          data: { id },
        });
        const d = (response.data.data && response.data.data[0]) || {};
        setTitle(d.title || "");
        setLocation(d.location || "");
        setEmploymentType(
          employmentTypeOptions.find((o) => o.value === d.employmentType) || {
            value: d.employmentType || "",
            label: d.employmentType || "",
          }
        );
        setExperience(d.experience || "");
        setEmail(d.email || "");
        setDatePosted((d.datePosted || "").slice(0, 10) || todayISO());
        setValidThrough((d.validThrough || "").slice(0, 10) || "");
        setStatus(
          statusOptions.find((o) => o.value === Number(d.status)) ||
            statusOptions[0]
        );
        setDescription(d.description || "");
        setLoading(false);
      } catch (error) {
        setLoading(false);
        Swal.fire({
          title: error?.response?.data?.message || "Failed to load job opening",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    if (!isAdd) fetchById();
  }, [isAdd, id, token]);

  const validateForm = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    if (!location.trim()) e.location = "Location is required";
    if (!employmentType?.value) e.employmentType = "Employment type is required";
    if (!String(experience).trim()) e.experience = "Experience is required";
    if (!email.trim()) e.email = "Contact email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      e.email = "Enter a valid email";
    if (!datePosted) e.datePosted = "Date posted is required";
    if (!description.trim()) e.description = "Description is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validateForm()) return;
    const payload = {
      title: title.trim(),
      description: DOMPurify.sanitize(description),
      datePosted,
      validThrough: validThrough || "",
      employmentType: employmentType.value,
      email: email.trim(),
      experience: String(experience).trim(),
      location: location.trim(),
      status: status.value,
    };
    if (!isAdd) payload.id = id;
    try {
      setLoading(true);
      const response = await axios({
        method: isAdd ? "post" : "patch",
        url: `${process.env.REACT_APP_API}admin/${
          isAdd ? "save-jobopeing" : "update-jobopeing"
        }`,
        headers: { "x-access-token": token, "Content-Type": "application/json" },
        data: payload,
      });
      setLoading(false);
      Swal.fire({
        title: response.data.message || "Saved",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/careers");
    } catch (error) {
      setLoading(false);
      Swal.fire({
        title: error?.response?.data?.message || "Failed to save job opening",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <PageShell>
      <PageHead title={isAdd ? "Add job opening" : "Edit job opening"} />
      <div className="new">
        <div className="newContainer">
          <Loader isLoading={loading} />
          <div className="bottom">
            <div>
              <form onSubmit={handleSubmit} className="faq-form">
                <div className="custome-form-group">
                  <label>Title*:</label>
                  <input
                    type="text"
                    value={title}
                    className="custome-input"
                    style={{ width: "524px" }}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setErrors({ ...errors, title: "" });
                    }}
                  />
                  {errors.title && (
                    <div className="invalid-feedback">{errors.title}</div>
                  )}
                </div>
                <div className="custome-form-group">
                  <label>Location*:</label>
                  <input
                    type="text"
                    value={location}
                    className="custome-input"
                    style={{ width: "524px" }}
                    placeholder="e.g. Toronto, ON / Remote"
                    onChange={(e) => {
                      setLocation(e.target.value);
                      setErrors({ ...errors, location: "" });
                    }}
                  />
                  {errors.location && (
                    <div className="invalid-feedback">{errors.location}</div>
                  )}
                </div>
                <div className="custome-form-group">
                  <label>Employment Type*:</label>
                  <Select
                    options={employmentTypeOptions}
                    value={employmentType}
                    onChange={(s) => {
                      setEmploymentType(s);
                      setErrors({ ...errors, employmentType: "" });
                    }}
                    className="sort-by dropdown country-drp"
                  />
                  {errors.employmentType && (
                    <div className="invalid-feedback">
                      {errors.employmentType}
                    </div>
                  )}
                </div>
                <div className="custome-form-group">
                  <label>Experience*:</label>
                  <input
                    type="text"
                    value={experience}
                    className="custome-input"
                    style={{ width: "524px" }}
                    placeholder="e.g. 3+ years"
                    onChange={(e) => {
                      setExperience(e.target.value);
                      setErrors({ ...errors, experience: "" });
                    }}
                  />
                  {errors.experience && (
                    <div className="invalid-feedback">{errors.experience}</div>
                  )}
                </div>
                <div className="custome-form-group">
                  <label>Contact Email*:</label>
                  <input
                    type="text"
                    value={email}
                    className="custome-input"
                    style={{ width: "524px" }}
                    placeholder="careers@rootsfundme.com"
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors({ ...errors, email: "" });
                    }}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
                <div className="custome-form-group">
                  <label>Date Posted*:</label>
                  <input
                    type="date"
                    value={datePosted}
                    className="custome-input"
                    onChange={(e) => {
                      setDatePosted(e.target.value);
                      setErrors({ ...errors, datePosted: "" });
                    }}
                  />
                  {errors.datePosted && (
                    <div className="invalid-feedback">{errors.datePosted}</div>
                  )}
                </div>
                <div className="custome-form-group">
                  <label>Valid Through (optional):</label>
                  <input
                    type="date"
                    value={validThrough}
                    className="custome-input"
                    onChange={(e) => setValidThrough(e.target.value)}
                  />
                </div>
                <div className="custome-form-group">
                  <label>Status*:</label>
                  <Select
                    options={statusOptions}
                    value={status}
                    onChange={(s) => setStatus(s)}
                    className="sort-by dropdown country-drp"
                  />
                </div>
                <div className="custome-form-group" style={{ width: "95%" }}>
                  <label>Description*:</label>
                  <CKEditor
                    editor={ClassicEditor}
                    data={description}
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      setDescription(data);
                      setErrors({
                        ...errors,
                        description: data.trim() ? "" : "Description is required",
                      });
                    }}
                  />
                  {errors.description && (
                    <div className="invalid-feedback">{errors.description}</div>
                  )}
                </div>
                <button type="submit" className="approveButton">
                  {isAdd ? "Save" : "Update"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CareersAddEdit;
